import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, Mail, Phone } from "lucide-react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        <Link to="/">
          <Button variant="ghost" size="sm" className="mb-6 text-green-700">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </Link>

        <Card className="border-0 shadow-xl rounded-2xl">
          <CardContent className="p-6 md:p-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="privacy-title">Privacy Policy</h1>
                <p className="text-sm text-muted-foreground">Last updated: April 2026</p>
              </div>
            </div>

            <div className="prose prose-sm max-w-none space-y-6 text-gray-700">
              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2">1. Information We Collect</h2>
                <p>When you book transportation with Easy Transportation, we collect:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li><strong>Personal Information:</strong> Full name, phone number (WhatsApp), email address</li>
                  <li><strong>Travel Details:</strong> Travel dates, flight numbers, airline, airport, destination/housing address</li>
                  <li><strong>Payment Information:</strong> Payment method (processed securely via Stripe, Zelle, or cash). We do not store credit card numbers.</li>
                  <li><strong>Country of Origin:</strong> Used for logistics coordination</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2">2. How We Use Your Information</h2>
                <ul className="list-disc pl-5 space-y-1">
                  <li>To confirm and manage your transportation booking</li>
                  <li>To send booking confirmations and updates via email and WhatsApp</li>
                  <li>To coordinate shared rides with other passengers traveling on the same date</li>
                  <li>To track flight arrivals for timely pickups</li>
                  <li>To process payments and provide receipts</li>
                  <li>To communicate with you about service changes or delays</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2">3. Information Sharing</h2>
                <p>We share your information only as needed to provide our services:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li><strong>Drivers:</strong> Your name, phone number, flight details, and pickup/dropoff locations are shared with your assigned driver</li>
                  <li><strong>Payment Processors:</strong> Stripe processes card payments under their own privacy policy</li>
                  <li><strong>Flight Data:</strong> We use AviationStack API to track flight status (no personal data is shared with them)</li>
                </ul>
                <p className="mt-2">We do <strong>not</strong> sell, rent, or trade your personal information to third parties for marketing purposes.</p>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2">4. Data Security</h2>
                <p>We implement industry-standard security measures to protect your data, including encrypted connections (HTTPS), secure authentication, and access controls. Payment processing is handled by PCI-compliant third-party providers.</p>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2">5. Data Retention</h2>
                <p>We retain booking records for up to 2 years for operational and accounting purposes. You may request deletion of your data by contacting us at any time.</p>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2">6. Cookies & Analytics</h2>
                <p>Our website uses essential cookies for functionality (login sessions, language preferences). We track basic page visit counts for service improvement. We do not use third-party advertising trackers.</p>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2">7. Your Rights</h2>
                <p>You have the right to:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>Request access to the personal data we hold about you</li>
                  <li>Request correction of inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Opt out of WhatsApp communications</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2">8. Children's Privacy</h2>
                <p>Our services are designed for J1 Visa holders (typically 18+). We do not knowingly collect information from children under 13.</p>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2">9. Changes to This Policy</h2>
                <p>We may update this privacy policy from time to time. Changes will be posted on this page with an updated revision date.</p>
              </section>

              <section className="bg-green-50 rounded-xl p-4 border border-green-100">
                <h2 className="text-lg font-bold text-gray-900 mb-2">10. Contact Us</h2>
                <p>If you have questions about this privacy policy or your data, contact us:</p>
                <div className="flex flex-col gap-2 mt-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-green-600" />
                    <a href="mailto:Miketransportsj1@gmail.com" className="text-green-700 hover:underline">Miketransportsj1@gmail.com</a>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-green-600" />
                    <a href="tel:+18159148139" className="text-green-700 hover:underline">(815) 914-8139</a>
                  </div>
                </div>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
